/**
 * The referral share message: the words a tenant's client sends a friend.
 *
 * Pure and shared on purpose. The server builds the ready-made options and
 * validates a save, the admin Settings card previews the result, and the
 * customer app composes what is really shared. All three read this file, so
 * the preview an owner approves is the text the friend receives, and the
 * length and plain-text rules can never differ between the card and the route.
 *
 * What an owner controls is the OPENING only. The code line and the sign-up
 * link are appended by `composeReferralShareText` / `referralShareUrl`, which
 * is why the link can't be edited out: it is never part of the stored message.
 *
 * Every string here is built from the tenant's own name, tagline and services.
 * Nothing names a specific tenant, and the platform's name never appears to a
 * tenant's client (#1321).
 */

/** Long enough for two sentences, short enough for a text message with a link after it. */
export const REFERRAL_SHARE_MESSAGE_MAX_LENGTH = 200;

/** A "what we offer" line naming more than this stops reading as a sentence. */
const MAX_SERVICES_IN_LINE = 4;

export type ReferralShareOptionKey = 'tagline' | 'offerings' | 'personal';

export interface ReferralShareOption {
  key: ReferralShareOptionKey;
  /** What the owner sees beside the radio. */
  label: string;
  /** The message itself, in this tenant's words. */
  text: string;
}

export interface ReferralShareOptionsInput {
  brandName: string;
  /** The tenant's tagline; blank means the tagline option leads with the name alone. */
  tagline?: string | null;
  /** Display names of what the business sells, in the owner's order. */
  serviceNames: readonly string[];
}

/** End a sentence with punctuation the shop may have left off. */
export function asSentence(s: string): string {
  return /[.!?]$/.test(s) ? s : `${s}.`;
}

/** "A", "A and B", "A, B, and C", with the serial comma. */
export function joinReferralServices(names: readonly string[]): string {
  if (names.length <= 2) return names.join(' and ');
  return `${names.slice(0, -1).join(', ')}, and ${names[names.length - 1]}`;
}

function offeringsText(brand: string, names: readonly string[]): string {
  return asSentence(`Check out ${brand}, ${joinReferralServices(names)}`);
}

/**
 * The ready-made messages for one tenant. `tagline` is always first and is
 * what a tenant gets without saving anything; the others are left out when
 * this tenant can't produce a message that would pass validation (no services
 * listed, or a name that fails the plain-text rule).
 */
export function referralShareOptions(input: ReferralShareOptionsInput): ReferralShareOption[] {
  const brand = input.brandName.trim();
  const tagline = input.tagline?.trim() ?? '';
  const lead = asSentence(`Check out ${brand}`);

  const services = input.serviceNames
    .map((s) => s.trim())
    .filter((s) => s.length > 0)
    .slice(0, MAX_SERVICES_IN_LINE);
  // A long catalogue loses names from the end rather than failing the cap.
  while (
    services.length > 1 &&
    offeringsText(brand, services).length > REFERRAL_SHARE_MESSAGE_MAX_LENGTH
  ) {
    services.pop();
  }

  const saved: ReferralShareOption[] = [];
  if (services.length > 0) {
    saved.push({
      key: 'offerings',
      label: 'Lead with what you offer',
      text: offeringsText(brand, services),
    });
  }
  saved.push({
    key: 'personal',
    label: 'A personal note from your client',
    text: `I take my dog to ${brand} and love it.`,
  });

  return [
    {
      key: 'tagline',
      label: tagline ? 'Lead with your tagline' : 'Lead with your name',
      text: tagline ? `${lead} ${asSentence(tagline)}` : lead,
    },
    ...saved.filter((o) => referralShareMessageProblem(o.text) === null),
  ];
}

/** Line breaks and control characters become single spaces: a share is one paragraph. */
export function cleanReferralShareMessage(raw: string): string {
  return raw.replace(/\p{Cc}/gu, ' ').replace(/\s+/g, ' ').trim();
}

/**
 * Why a CLEANED message can't be saved, as a sentence for the owner, or null
 * when it is fine. The one rule set, read by the card and the PUT boundary.
 */
export function referralShareMessageProblem(cleaned: string): string | null {
  if (cleaned.length === 0) return 'Write a message, or pick one of the options.';
  if (cleaned.length > REFERRAL_SHARE_MESSAGE_MAX_LENGTH) {
    return `Keep it to ${REFERRAL_SHARE_MESSAGE_MAX_LENGTH} characters.`;
  }
  if (/<[a-z/!]/i.test(cleaned)) return 'Plain words only. Take out the tags in angle brackets.';
  if (/animal\s*lovers\s*app|animalloversapp/i.test(cleaned)) {
    return 'Use your own business name here. Your clients never see the name of the app.';
  }
  return null;
}

/** The fixed sentence every share carries; null when the client has no code yet. */
export function referralShareCodeLine(code: string): string | null {
  return code ? `Use my code ${code} when you sign up.` : null;
}

/**
 * /register?ref= is read + validated by the customer app's Register page;
 * /claim is the existing-account flow and a dead end for a referred newcomer.
 */
export function referralShareUrl(origin: string, code: string): string {
  return code ? `${origin}/register?ref=${encodeURIComponent(code)}` : `${origin}/register`;
}

/** The body of a share with a chosen message: the owner's opening, then the code line. */
export function composeReferralShareText(message: string, code: string): string {
  return [asSentence(message), referralShareCodeLine(code)]
    .filter((s): s is string => s !== null)
    .join(' ');
}
